import { useEffect, useState } from "react";
import { Eye } from "lucide-react";

import { DataTable } from "components/ui/DataTable/DataTable";
import { HideReviewedToggle } from "features/results/components/HideReviewedToggle/HideReviewedToggle";
import { ResultCorrectionBadge } from "features/results/components/ResultCorrectionBadge/ResultCorrectionBadge";
import { PATIENT_RESULTS_TYPE_FILTERS } from "features/results/constants/messageTypeGroups";
import {
  DEFAULT_ROWS_PER_PAGE,
  ROWS_PER_PAGE_OPTIONS,
} from "features/results/constants/pagination";
import { usePatientResultsData } from "features/results/hooks/usePatientResultsData";
import { formatCommonDate } from "features/results/utils/formatters";
import {
  BABY_DISCHARGE_LABEL,
  isBabyDischargeSummary,
} from "features/results/utils/neonatalDischarge";
import { isSupersededResult } from "features/results/utils/resultCorrection";
import { useDebounce } from "hooks/useDebounce";

import style from "./PatientResultsTable.module.css";

// Type-group options for the table's native "Filters" control. "All Results"
// is included as an explicit option and is the default selection.
const FILTERS_CONFIG = PATIENT_RESULTS_TYPE_FILTERS.map((option) => ({
  id: option.value,
  label: option.label,
}));

// Append the test names to the type so a lab/rad row is identifiable without
// opening it (e.g. "Radiology - US Pelvis and Renal"). The DataTable cell
// truncates with an ellipsis + tooltip when it overflows.
const withTestNames = (baseLabel, separator, row) => {
  const names = row.testNamesList?.filter(Boolean).join(", ");

  return names ? `${baseLabel}${separator}${names}` : baseLabel;
};

// Friendly type label for a common-backed row (lab/rad rows are labelled from
// the active filter instead, since dedicated endpoints carry no resultType).
const getCommonTypeLabel = (row) => {
  const resultType = (row.resultType || "").toUpperCase();

  if (resultType.includes("LAB")) {
    return withTestNames("Laboratory result", ": ", row);
  }

  if (resultType.includes("RAD")) return withTestNames("Radiology", " - ", row);

  return row.resultType || "-";
};

export const PatientResultsTable = ({
  patientId,
  onSelectItem,
  selectedItemId,
  title,
  onResultsLoaded,
}) => {
  const [search, setSearch] = useState("");
  // Single-select type filter (acts like the global Results tab switch);
  // defaults to "All Results".
  const [filters, setFilters] = useState(["all-results"]);
  const [hideReviewed, setHideReviewed] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const selectedType = filters[0] ?? "all-results";

  // Reset the search when switching type so you don't carry a query into a
  // filter that doesn't match it.
  useEffect(() => {
    setSearch("");
  }, [selectedType]);

  const activeGroup = PATIENT_RESULTS_TYPE_FILTERS.find(
    (option) => option.value === selectedType
  );
  const { messageTypes } = activeGroup ?? { messageTypes: null };

  // Lab/Radiology use their dedicated patient endpoints; everything else
  // (All + the message-type groups) goes through the common endpoint.
  const tableType =
    selectedType === "laboratory"
      ? "laboratory"
      : selectedType === "radiology"
      ? "radiology"
      : "common";

  // Server-side search (BE requires >= 3 chars) for every type.
  const debouncedSearch = useDebounce(search, 400);
  const trimmedSearch = debouncedSearch.trim();
  const searchQuery = trimmedSearch.length >= 3 ? trimmedSearch : undefined;

  // "Hide reviewed" is now a server-side filter (unreviewed=true → non-CONFIRMED).
  const unreviewed = hideReviewed ? true : undefined;

  // Reset to the first page whenever the query changes, so we never sit on an
  // out-of-range page after the result set shrinks.
  useEffect(() => {
    setPage(1);
  }, [selectedType, searchQuery, unreviewed]);

  const {
    results,
    totalCount,
    serverPaginated,
    isLoading,
    isError,
    isLaboratory,
    isRadiology,
  } = usePatientResultsData({
    type: tableType,
    patientId,
    messageTypes,
    searchQuery,
    unreviewed,
    limit: rowsPerPage,
    offset: (page - 1) * rowsPerPage,
    orderBy: "DESC",
  });

  const resultKind = isLaboratory ? "lab" : isRadiology ? "rad" : "common";

  const getTypeLabel = (row) => {
    if (isLaboratory) return withTestNames("Laboratory result", ": ", row);
    if (isRadiology) return withTestNames("Radiology", " - ", row);
    return getCommonTypeLabel(row);
  };

  /*
   * On this screen every row already belongs to the patient whose file it is, so there is no
   * patient column to hang it from — the label goes beside the type, which is the only thing
   * distinguishing one document from another here (KIN-1949).
   */
  const renderType = (row) => (
    <span className={style.typeCell}>
      <span className={style.typeText}>{getTypeLabel(row)}</span>
      {!!isBabyDischargeSummary(row) && (
        <span className={style.babyDischarge}>{BABY_DISCHARGE_LABEL}</span>
      )}
      <ResultCorrectionBadge result={row} />
    </span>
  );

  // Filtering (patientId scope + messageTypes), search and pagination are all
  // server-side now (BE returns the requested page + totalCount).
  const filteredData = results;

  /*
   * The page of rows, handed up so the detail can resolve a correction's counterpart (KIN-2089).
   */
  useEffect(() => {
    onResultsLoaded?.(results);
  }, [results, onResultsLoaded]);

  const columns = [
    {
      id: "date",
      header: "Date",
      width: "120px",
      render: (row) => formatCommonDate(row.orderDate),
    },
    {
      id: "type",
      header: "Type",
      flexGrow: 1,
      // Base width 0 → minmax(0, 1fr) so a long "Type" (lab/rad test names)
      // truncates with an ellipsis instead of stretching the table.
      width: "0",
      render: renderType,
    },
  ];

  const rowActions = [
    {
      label: "View",
      icon: Eye,
      primary: true,
      onClick: (row) => onSelectItem?.(row, resultKind),
    },
  ];

  return (
    <div className={style.card}>
      {!!title && <h1 className={style.title}>{title}</h1>}
      <DataTable
        columns={columns}
        data={filteredData}
        noScroll
        getRowId={(row) => row.id}
        isLoading={isLoading}
        totalCount={serverPaginated ? totalCount : undefined}
        rowsOptions={ROWS_PER_PAGE_OPTIONS}
        rowsValue={rowsPerPage}
        onRowsChange={(value) => {
          setRowsPerPage(value);
          setPage(1);
        }}
        pagination={{ page, onPageChange: setPage }}
        getRowClassName={(row) =>
          [
            row.id === selectedItemId ? style.selectedRow : undefined,
            // Struck through, so its numbers cannot be read as current (KIN-2089).
            isSupersededResult(row) ? style.supersededRow : undefined,
          ]
            .filter(Boolean)
            .join(" ") || undefined
        }
        onRowClick={(row) => onSelectItem?.(row, resultKind)}
        emptyTitle={isError ? "Error loading results" : "No items found"}
        emptyText={
          isError
            ? "Unable to load results. Please try again later."
            : search || selectedType !== "all-results"
            ? "Try adjusting your search or filters to find what you're looking for."
            : "There are no results to display at this time."
        }
        search={{
          value: search,
          onChange: setSearch,
          placeholder: "Search",
          maxWidth: "210px",
        }}
        filtersConfig={FILTERS_CONFIG}
        filters={{
          value: filters,
          // Keep it single-select: only the most recently picked option stays.
          onChange: (_groupId, values) => setFilters(values.slice(-1)),
        }}
        toolbarExtra={
          <HideReviewedToggle value={hideReviewed} onChange={setHideReviewed} />
        }
        rowActions={rowActions}
        actionsColumnWidth="32px"
      />
    </div>
  );
};
